import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Colors } from "../../utils/Colors";
import { ROUTES } from "../../shared/constants/routes";

const HISTORY_API = "http://127.0.0.1:8000/api/investigations/patient-history/";

const thStyle = {
  textAlign: "left",
  padding: "14px",
  background: Colors.bg.hover,
  color: Colors.text.header,
  fontSize: "13px",
  borderBottom: `1px solid ${Colors.border.light}`,
  fontWeight: 800,
};

const tdStyle = {
  padding: "14px",
  borderBottom: `1px solid ${Colors.border.light}`,
  fontSize: "14px",
  color: Colors.text.body,
};

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export default function InvestigationPatientHistoryDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientUhid } = useParams();

  const passedPatient = location.state?.patient || null;

  const [selectedPatient, setSelectedPatient] = useState(passedPatient);
  const [historyRows, setHistoryRows] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (patientUhid) {
      fetchPatientHistory(patientUhid);
    }
  }, [patientUhid]);

  const fetchPatientHistory = async (uhid) => {
    try {
      setLoadingHistory(true);
      setErrorMsg("");

      const res = await axios.get(`${HISTORY_API}?patient_uhid=${encodeURIComponent(uhid)}`);

      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
        ? res.data.results
        : [];

      setHistoryRows(data);

      if (!passedPatient && data.length > 0) {
        setSelectedPatient({
          patient_uhid: data[0].patient_uhid,
          patient_name: data[0].patient_name,
          admission_number: data[0].admission_number,
          doctor_name: data[0].doctor_name,
          total_investigations: data.length,
          last_investigation_at: data[0].created_at,
        });
      }
    } catch (error) {
      console.error("History fetch error:", error);

      if (error.response?.data) {
        setErrorMsg(
          typeof error.response.data === "string"
            ? error.response.data
            : JSON.stringify(error.response.data)
        );
      } else {
        setErrorMsg("Unable to load patient investigation history.");
      }

      setHistoryRows([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: Colors.bg.app, padding: "32px 18px" }}>
      <div
        style={{
          maxWidth: "1380px",
          margin: "0 auto",
          background: Colors.bg.paper,
          borderRadius: "24px",
          boxShadow: Colors.shadow.brand,
          overflow: "hidden",
          border: `1px solid ${Colors.border.light}`,
        }}
      >
        <div style={{ padding: "26px 30px 18px", borderBottom: `1px solid ${Colors.border.light}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
            <h2 style={{ margin: 0, fontSize: "28px", fontWeight: 800, color: Colors.text.title }}>
              Patient Investigation History
            </h2>

            <button
              type="button"
              onClick={() => navigate(ROUTES.INVESTIGATION_HISTORY)}
              style={{
                borderRadius: "12px",
                border: `1px solid ${Colors.border.input}`,
                background: Colors.bg.paper,
                color: Colors.text.body,
                fontWeight: 700,
                cursor: "pointer",
                padding: "12px 16px",
              }}
            >
              Back to Summary
            </button>
          </div>
        </div>

        <div style={{ padding: "28px" }}>
          {errorMsg && (
            <div
              style={{
                marginBottom: "18px",
                padding: "13px 14px",
                background: "#fff4f4",
                border: `1px solid ${Colors.status.danger}`,
                color: Colors.status.danger,
                borderRadius: "12px",
              }}
            >
              {errorMsg}
            </div>
          )}

          {selectedPatient && (
            <div
              style={{
                marginBottom: "24px",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "14px",
              }}
            >
              <div style={{ background: Colors.bg.hover, borderRadius: "14px", padding: "14px" }}>
                <div style={{ fontSize: "12px", color: Colors.text.sub, fontWeight: 700 }}>Patient UHID</div>
                <div style={{ color: Colors.text.title, fontWeight: 800, marginTop: "4px" }}>
                  {selectedPatient.patient_uhid || "-"}
                </div>
              </div>

              <div style={{ background: Colors.bg.hover, borderRadius: "14px", padding: "14px" }}>
                <div style={{ fontSize: "12px", color: Colors.text.sub, fontWeight: 700 }}>Patient Name</div>
                <div style={{ color: Colors.text.title, fontWeight: 800, marginTop: "4px" }}>
                  {selectedPatient.patient_name || "-"}
                </div>
              </div>

              <div style={{ background: Colors.bg.hover, borderRadius: "14px", padding: "14px" }}>
                <div style={{ fontSize: "12px", color: Colors.text.sub, fontWeight: 700 }}>Admission Number</div>
                <div style={{ color: Colors.text.title, fontWeight: 800, marginTop: "4px" }}>
                  {selectedPatient.admission_number || "-"}
                </div>
              </div>

              <div style={{ background: Colors.bg.hover, borderRadius: "14px", padding: "14px" }}>
                <div style={{ fontSize: "12px", color: Colors.text.sub, fontWeight: 700 }}>Doctor Name</div>
                <div style={{ color: Colors.text.title, fontWeight: 800, marginTop: "4px" }}>
                  {selectedPatient.doctor_name || "-"}
                </div>
              </div>
            </div>
          )}

          <div style={{ overflowX: "auto", border: `1px solid ${Colors.border.light}`, borderRadius: "16px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1600px" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Admission No</th>
                  <th style={thStyle}>Patient UHID</th>
                  <th style={thStyle}>Patient Name</th>
                  <th style={thStyle}>Doctor Name</th>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Test Name</th>
                  <th style={thStyle}>Body Part / Sample</th>
                  <th style={thStyle}>Result</th>
                  <th style={thStyle}>Impression / Diagnosis</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Remarks</th>
                  <th style={thStyle}>Unit</th>
                  <th style={thStyle}>Normal Range</th>
                  <th style={thStyle}>Abnormal Flag</th>
                  <th style={thStyle}>Ordered By</th>
                  <th style={thStyle}>Performed By</th>
                  <th style={thStyle}>Created At</th>
                </tr>
              </thead>

              <tbody>
                {loadingHistory ? (
                  <tr>
                    <td colSpan="18" style={tdStyle}>
                      Loading history...
                    </td>
                  </tr>
                ) : historyRows.length === 0 ? (
                  <tr>
                    <td colSpan="18" style={tdStyle}>
                      No history found.
                    </td>
                  </tr>
                ) : (
                  historyRows.map((row) => (
                    <tr key={row.id}>
                      <td style={tdStyle}>{row.admission_number || "-"}</td>
                      <td style={tdStyle}>{row.patient_uhid || "-"}</td>
                      <td style={tdStyle}>{row.patient_name || "-"}</td>
                      <td style={tdStyle}>{row.doctor_name || "-"}</td>
                      <td style={tdStyle}>{row.category_label || row.category || "-"}</td>
                      <td style={tdStyle}>{row.test_name || "-"}</td>
                      <td style={tdStyle}>{row.body_part_sample || "-"}</td>
                      <td style={tdStyle}>{row.result || "-"}</td>
                      <td style={tdStyle}>{row.impression_diagnosis || "-"}</td>
                      <td style={tdStyle}>{row.status_label || row.status || "-"}</td>
                      <td style={tdStyle}>{row.date || "-"}</td>
                      <td style={tdStyle}>{row.remarks || "-"}</td>
                      <td style={tdStyle}>{row.unit || "-"}</td>
                      <td style={tdStyle}>{row.normal_range || "-"}</td>
                      <td style={tdStyle}>{row.abnormal_flag_label || row.abnormal_flag || "-"}</td>
                      <td style={tdStyle}>{row.ordered_by || "-"}</td>
                      <td style={tdStyle}>{row.performed_by || "-"}</td>
                      <td style={tdStyle}>{formatDateTime(row.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}